/* eslint-disable react/no-children-prop */
import {
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Box,
  Flex,
  Stack,
  Select,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Spinner,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { format } from 'date-fns';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Briefcase, Edit, Mail, Phone, Trash, User } from 'react-feather';
//import debounce from 'lodash.debounce';

import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { SortingRule } from 'react-table';
import ErrorMessage from '../../../components/ErrorMessage';
import RegisterHeader from '../../../components/RegisterHeader';
import { ICustomerDTO } from '../../../dtos/ICustomerDTO';
import { IUserDTO } from '../../../dtos/IUserDTO';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import {
  getRightNumber,
  getRightLabel,
  usersRights,
} from '../../../utils/userRights';
import { exportData } from '../../../utils/xlsx';
import { isPersonalEmailDomain } from '../../../utils/email';
import { formatPhone, onlyPhoneDigits } from '../../../utils/phone';
import { IResponseWithPagination } from '../../../dtos/IPaginationDTO';

import { ReactTable, TableFooter, SearchInput } from '../../../components';
import useReactTableInstance, {
  Data,
} from '../../../hooks/useReactTableInstance';

import { Container } from './styles';

import { useLoading } from '../../../hooks/loading';

const SORT_BY_STATE = [{ id: 'name' }];
const CUSTOMER_PAGE_SIZE = 20;

type CustomerResumedState = Pick<
  ICustomerDTO,
  'id' | 'tradeName' | 'internalCode' | 'cnpj'
>;

const Users: React.FC = () => {
  const { addToast } = useToast();
  const { isLoading: isRequestLoading } = useLoading();
  const [users, setUsers] = useState<IUserDTO[]>([]);
  const [selectedUser, setSelectedUser] = useState<IUserDTO>();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [passwordResetModalIsOpen, setPasswordResetModalIsOpen] = useState(
    false,
  );
  const [newPassword, setNewPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [isPasswordResetting, setIsPasswordResetting] = useState(false);
  const [companySelectorIsOpen, setCompanySelectorIsOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [customerPageIndex, setCustomerPageIndex] = useState(0);
  const [customerTotalCount, setCustomerTotalCount] = useState(0);
  const [customers, setCustomers] = useState<CustomerResumedState[]>([]);
  const [
    selectedCustomer,
    setSelectedCustomer,
  ] = useState<CustomerResumedState>();
  const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [queryTotalCount, setQueryTotalCount] = useState<number>(0);
  const [queryPageIndex, setQueryPageIndex] = useState(0);
  const [queryPageSize] = useState(20);
  const [querySortBy, setQuerySortBy] = useState<SortingRule<Data>[]>(
    SORT_BY_STATE,
  );

  const queryTotalPageCount = Math.ceil(queryTotalCount / queryPageSize);
  const customerTotalPageCount = Math.ceil(
    customerTotalCount / CUSTOMER_PAGE_SIZE,
  );

  const [searchString, setSearchString] = useState('');
  const [searchField, setSearchField] = useState('');

  const toggleDeleteDialog = () => {
    setDeleteDialogIsOpen(!deleteDialogIsOpen);
  };

  const validationSchema = useMemo(() => {
    return yup.object().shape({
      name: yup.string().required('Campo obrigatório'),
      email: yup
        .string()
        .email('E-mail inválido')
        .test(
          'corporate-email',
          'Use um e-mail corporativo; provedores pessoais não são permitidos',
          value => !value || !isPersonalEmailDomain(value),
        )
        .required('Campo obrigatório'),
      company: yup.string(),
      customer_id: yup.string().nullable(),
      rights: yup.string(),
      phone: yup
        .string()
        .transform(value => onlyPhoneDigits(value))
        .required('Campo obrigatório')
        .matches(/^\d{10,11}$/, 'Número de telefone inválido'),
    });
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<IUserDTO>({
    resolver: yupResolver(validationSchema),
  });

  const formattedUsers = useMemo(() => {
    return users.map(
      ({ id, name, email, phone, company, rights, customer_id }) => ({
        id,
        name,
        email,
        phone,
        company,
        customer_id,
        rights: getRightLabel(rights),
      }),
    );
  }, [users]);

  useEffect(() => {
    if (selectedCustomer) {
      setValue('customer_id', selectedCustomer.id);
      setValue('company', selectedCustomer.tradeName);
    }
  }, [selectedCustomer, setValue]);

  const columns = useMemo(() => {
    return [
      {
        accessor: 'name',
        Header: 'Nome',
      },
      {
        accessor: 'email',
        Header: 'E-mail',
      },
      {
        accessor: 'phone',
        Header: 'Telefone',
      },
      {
        accessor: 'company',
        Header: 'Empresa',
      },
      {
        accessor: 'rights',
        Header: 'Permissões',
      },
    ];
  }, []);

  const fetchUsers = useCallback((signal?: AbortSignal) => {
    const sort_by = querySortBy[0].id;
    const order_by = querySortBy[0].desc ? 'DESC' : 'ASC';

    api
      .get<IResponseWithPagination<IUserDTO>>('/users', {
        params: {
          limit: queryPageSize,
          offset: queryPageIndex * queryPageSize,
          sort_by,
          order_by,
          q: searchString,
          search_field: searchField || undefined,
        },
        signal,
      })
      .then(response => {
        const { records, total_results } = response.data;
        setQueryTotalCount(total_results);
        setUsers(records);
      })
      .catch(error => {
        if (error.name !== 'CanceledError') console.error(error);
      });
  }, [queryPageIndex, queryPageSize, querySortBy, searchString, searchField]);

  // const debouncedFetch = useMemo(() => debounce(fetchUsers, 1000), [
  //   fetchUsers,
  // ]);

  useEffect(() => {
    const controller = new AbortController();
    fetchUsers(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPageIndex, querySortBy, searchString]);

  useEffect(() => {
    if (!companySelectorIsOpen) return undefined;

    const controller = new AbortController();

    api
      .get<IResponseWithPagination<CustomerResumedState>>('/customers', {
        params: {
          limit: CUSTOMER_PAGE_SIZE,
          offset: customerPageIndex * CUSTOMER_PAGE_SIZE,
          sort_by: 'tradeName',
          order_by: 'ASC',
          q: companySearch,
        },
        signal: controller.signal,
      })
      .then(response => {
        const { records, total_results } = response.data;
        setCustomers(records);
        setCustomerTotalCount(total_results);
      })
      .catch(error => {
        if (error.name !== 'CanceledError') console.error(error);
      });

    return () => controller.abort();
  }, [companySelectorIsOpen, companySearch, customerPageIndex]);

  const {
    canNextPage,
    canPreviousPage,
    getTableProps,
    gotoPage,
    headerGroups,
    nextPage,
    page,
    pageCount,
    pageIndex,
    pageOptions,
    prepareRow,
    previousPage,
    sortBy,
  } = useReactTableInstance({
    columns,
    data: formattedUsers,
    pagination: {
      pageCount: queryTotalPageCount,
      pageIndex: queryPageIndex,
      pageSize: queryPageSize,
    },
    sort: {
      sortBy: querySortBy,
    },
  });

  useEffect(() => {
    setQueryPageIndex(pageIndex);
  }, [pageIndex]);

  useEffect(() => {
    setQuerySortBy(sortBy.length ? sortBy : SORT_BY_STATE);
  }, [sortBy]);

  const handleExportData = useCallback(() => {
    const data = users.map(customer => [
      customer.name,
      customer.email,
      customer.phone,
      customer.company,
    ]);

    exportData({
      columns: ['Nome', 'E-mail', 'Telefone', 'Empresa'],
      rows: data,
      filename: `relatorio_clientes_${format(new Date(), 'yyyy-MM-dd')}`,
    });
  }, [users]);

  const toggleModal = () => {
    clearErrors();
    reset();
    setSelectedCustomer(undefined);
    setCompanySelectorIsOpen(false);
    setCompanySearch('');
    setPasswordResetModalIsOpen(false);
    setNewPassword('');
    setPasswordConfirmation('');
    setGeneratedPassword('');
    setModalIsOpen(!modalIsOpen);
  };

  const handleEdit = (data: IUserDTO) => {
    // reset();
    toggleModal();
    const formattedUser = {
      ...data,
      phone: formatPhone(data.phone),
      rights: getRightNumber(String(data.rights)) || 3,
    };

    setSelectedUser(formattedUser);
    setSelectedCustomer(
      customers.find(customer => customer.id === data.customer_id),
    );

    reset(formattedUser);
  };

  const handleOpenCompanySelector = () => {
    setCompanySearch('');
    setCustomerPageIndex(0);
    setCompanySelectorIsOpen(true);
  };

  const handleSelectCustomer = (customer: CustomerResumedState) => {
    setSelectedCustomer(customer);
    setCompanySelectorIsOpen(false);
  };

  const closePasswordResetModal = () => {
    setPasswordResetModalIsOpen(false);
    setNewPassword('');
    setPasswordConfirmation('');
    setGeneratedPassword('');
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    if (newPassword.length < 8) {
      addToast({
        title: 'Erro',
        description: 'A senha deve ter pelo menos 8 caracteres',
        type: 'error',
      });
      return;
    }

    if (newPassword !== passwordConfirmation) {
      addToast({
        title: 'Erro',
        description: 'As senhas não conferem',
        type: 'error',
      });
      return;
    }

    setIsPasswordResetting(true);
    try {
      await api.patch(`/users/${selectedUser.id}/password`, {
        mode: 'manual',
        password: newPassword,
        password_confirmation: passwordConfirmation,
      });

      addToast({
        title: 'Sucesso',
        description: 'Senha redefinida com sucesso',
        type: 'success',
      });
      closePasswordResetModal();
    } catch (error: any) {
      addToast({
        title: 'Erro',
        description:
          error.response?.data?.message ||
          'Não foi possível redefinir a senha',
        type: 'error',
      });
    } finally {
      setIsPasswordResetting(false);
    }
  };

  const handleGeneratePassword = async () => {
    if (!selectedUser) return;

    setIsPasswordResetting(true);
    try {
      const response = await api.patch(`/users/${selectedUser.id}/password`, {
        mode: 'generated',
      });

      setGeneratedPassword(response.data.temporary_password);
      addToast({
        title: 'Sucesso',
        description: 'A senha temporária foi gerada',
        type: 'success',
      });
    } catch (error: any) {
      addToast({
        title: 'Erro',
        description:
          error.response?.data?.message ||
          'Não foi possível gerar a senha',
        type: 'error',
      });
    } finally {
      setIsPasswordResetting(false);
    }
  };

  const handleCopyGeneratedPassword = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword);
      addToast({
        title: 'Sucesso',
        description: 'Senha copiada para a área de transferência',
        type: 'success',
      });
    } catch {
      addToast({
        title: 'Erro',
        description: 'Não foi possível copiar a senha automaticamente',
        type: 'error',
      });
    }
  };

  const handleClickButtonDelete = (data: IUserDTO) => {
    setSelectedUser(data);
    toggleDeleteDialog();
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      if (selectedUser) {
        await api.delete(`/users/${selectedUser.id}`);

        const newUsersArray = users.filter(user => user.id !== selectedUser.id);

        toggleDeleteDialog();
        setUsers(newUsersArray);
        setSelectedUser(undefined);

        addToast({
          title: 'Sucesso',
          description: 'Usuário deletado com sucesso',
          type: 'success',
        });
      }
    } catch (error: any) {
      if (error.response && error.response.data) {
        addToast({
          title: 'Error',
          description: error.response.data,
          type: 'error',
        });
      } else {
        addToast({
          title: 'Error',
          description: 'Ocorreu um erro inesperado, tente novamente mais tarde',
          type: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: IUserDTO) => {
    try {
      const response = await api.put('/users', {
        id: selectedUser?.id,
        name: data.name,
        company: data.company,
        customer_id: data.customer_id,
        email: data.email,
        phone: onlyPhoneDigits(data.phone),
        rights: data.rights,
      });

      const newUsersArray = users.filter(user => user.id !== selectedUser?.id);

      newUsersArray.push(response.data);

      setUsers(newUsersArray);

      toggleModal();
    } catch (error: any) {
      if (error.response && error.response.data) {
        addToast({
          title: 'Error',
          description: error.response.data,
          type: 'error',
        });
      }
      addToast({
        title: 'Error',
        description: 'Ocorreu um erro inesperado, tente novamente mais tarde',
        type: 'error',
      });
    }
  };

  return (
    <Container>
      <AlertDialog
        isOpen={deleteDialogIsOpen}
        leastDestructiveRef={cancelRef}
        onClose={toggleDeleteDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Deletar usuário
            </AlertDialogHeader>

            <AlertDialogBody>
              Você deseja deletar esse usuário? Essa ação não poderá ser
              revertida depois.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={toggleDeleteDialog}>
                Cancelar
              </Button>
              <Button
                isLoading={isLoading}
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
              >
                Deletar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <Modal isOpen={modalIsOpen} onClose={toggleModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar usuário</ModalHeader>
          <ModalCloseButton />
          <Stack as="form" onSubmit={handleSubmit(onSubmit)}>
            <ModalBody pb={6}>
              <FormControl>
                <FormLabel>Nome</FormLabel>
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    children={<User color="#0085ae" />}
                  />
                  <Input isInvalid={!!errors.name} {...register('name')} />
                </InputGroup>
                <Box mt={2}>
                  <ErrorMessage message={errors.name?.message} />
                </Box>
              </FormControl>
              <FormControl>
                <FormLabel>E-mail</FormLabel>
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    children={<Mail color="#0085ae" />}
                  />
                  <Input
                    isInvalid={!!errors.email}
                    {...register('email')}
                  />
                </InputGroup>
                <Box mt={2}>
                  <ErrorMessage message={errors.email?.message} />
                </Box>
              </FormControl>
              <FormControl>
                <FormLabel>Telefone</FormLabel>
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    children={<Phone color="#0085ae" />}
                  />
                  <Input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={15}
                    isInvalid={!!errors.phone}
                    {...register('phone', {
                      onChange: event => {
                        event.target.value = formatPhone(event.target.value);
                      },
                    })}
                  />
                </InputGroup>
                <Box mt={2}>
                  <ErrorMessage message={errors.phone?.message} />
                </Box>
              </FormControl>

              <FormControl>
                <FormLabel>Nome da Empresa</FormLabel>
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    children={<Briefcase color="#0085ae" />}
                  />
                  <Input
                    type="tel"
                    isInvalid={!!errors.company}
                    {...register('company')}
                  />
                </InputGroup>
                <Box mt={2}>
                  <ErrorMessage message={errors.company?.message} />
                </Box>
              </FormControl>
              <FormControl>
                <FormLabel>Empresa</FormLabel>
                <Input type="hidden" {...register('customer_id')} />
                <Button
                  type="button"
                  width="100%"
                  variant="outline"
                  justifyContent="flex-start"
                  onClick={handleOpenCompanySelector}
                >
                  <Box
                    as="span"
                    width="100%"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    textAlign="left"
                    title={
                      selectedCustomer
                        ? `${selectedCustomer.tradeName} - ${selectedCustomer.internalCode} - ${selectedCustomer.cnpj}`
                        : selectedUser?.company || 'Selecione uma empresa'
                    }
                  >
                    {selectedCustomer
                      ? `${selectedCustomer.tradeName} - ${selectedCustomer.internalCode} - ${selectedCustomer.cnpj}`
                      : selectedUser?.company || 'Selecione uma empresa'}
                  </Box>
                </Button>
                <Box mt={2}>
                  <ErrorMessage message={errors.customer_id?.message} />
                </Box>
              </FormControl>
              <FormControl>
                <FormLabel>Permissões</FormLabel>
                <Select
                  defaultValue={selectedUser?.rights}
                  {...register('rights')}
                >
                  {usersRights.map(right => (
                    <option key={right.right} value={right.right}>
                      {right.label}
                    </option>
                  ))}
                </Select>
                <Box mt={2}>
                  <ErrorMessage message={errors.rights?.message} />
                </Box>
              </FormControl>
            </ModalBody>

            <ModalFooter>
              <Button
                type="button"
                variant="outline"
                colorScheme="orange"
                mr="auto"
                onClick={() => setPasswordResetModalIsOpen(true)}
              >
                Redefinir senha
              </Button>
              <Button
                isLoading={isSubmitting}
                colorScheme="blue"
                mr={3}
                type="submit"
              >
                Salvar
              </Button>
              <Button onClick={toggleModal}>Cancelar</Button>
            </ModalFooter>
          </Stack>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={passwordResetModalIsOpen}
        onClose={closePasswordResetModal}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Redefinir senha</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {generatedPassword ? (
              <>
                <FormLabel>Senha temporária</FormLabel>
                <Input value={generatedPassword} isReadOnly />
                <Box mt={3} color="orange.600" fontSize="sm">
                  Copie esta senha agora. Ela não será exibida novamente.
                </Box>
              </>
            ) : (
              <>
                <FormControl>
                  <FormLabel>Nova senha</FormLabel>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={event => setNewPassword(event.target.value)}
                  />
                </FormControl>
                <FormControl mt={4}>
                  <FormLabel>Confirme a nova senha</FormLabel>
                  <Input
                    type="password"
                    value={passwordConfirmation}
                    onChange={event =>
                      setPasswordConfirmation(event.target.value)
                    }
                  />
                </FormControl>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {generatedPassword ? (
              <>
                <Button type="button" onClick={handleCopyGeneratedPassword}>
                  Copiar senha
                </Button>
                <Button ml={3} onClick={closePasswordResetModal}>
                  Fechar
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGeneratePassword}
                  isLoading={isPasswordResetting}
                >
                  Gerar senha aleatória
                </Button>
                <Button
                  type="button"
                  colorScheme="blue"
                  ml={3}
                  onClick={handleResetPassword}
                  isLoading={isPasswordResetting}
                >
                  Redefinir senha
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={companySelectorIsOpen}
        onClose={() => setCompanySelectorIsOpen(false)}
      >
        <ModalOverlay />
        <ModalContent maxW="720px" width="calc(100% - 32px)">
          <ModalHeader>Selecionar empresa</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Input
              autoFocus
              placeholder="Busque pelo nome da empresa"
              value={companySearch}
              onChange={event => {
                setCompanySearch(event.target.value);
                setCustomerPageIndex(0);
              }}
            />
            <Box mt={4} borderWidth="1px" borderRadius="md" overflow="hidden">
              <Box
                display="grid"
                gridTemplateColumns="110px minmax(0, 1fr) 150px"
                gap={3}
                px={3}
                py={2}
                bg="gray.50"
                borderBottomWidth="1px"
                color="gray.600"
                fontSize="xs"
                fontWeight="bold"
                textTransform="uppercase"
              >
                <Box>Código</Box>
                <Box>Nome da empresa</Box>
                <Box>CNPJ</Box>
              </Box>
              <Stack spacing={0} maxH="320px" overflowY="auto">
                {customers.length ? (
                  customers.map(customer => (
                    <Button
                      key={customer.id}
                      type="button"
                      variant="ghost"
                      display="grid"
                      gridTemplateColumns="110px minmax(0, 1fr) 150px"
                      gap={3}
                      px={3}
                      borderRadius={0}
                      borderBottomWidth="1px"
                      borderColor="gray.100"
                      height="auto"
                      minH="48px"
                      py={3}
                      fontWeight="normal"
                      _hover={{ bg: 'blue.50' }}
                      onClick={() => handleSelectCustomer(customer)}
                    >
                      <Box textAlign="left">{customer.internalCode}</Box>
                      <Box
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        textAlign="left"
                        title={customer.tradeName}
                      >
                        {customer.tradeName}
                      </Box>
                      <Box textAlign="left">{customer.cnpj}</Box>
                    </Button>
                  ))
                ) : (
                  <Box px={3} py={4} color="gray.500">
                    Nenhuma empresa encontrada.
                  </Box>
                )}
              </Stack>
            </Box>
            {customerTotalCount > CUSTOMER_PAGE_SIZE && (
              <Flex mt={4} align="center" justify="space-between">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setCustomerPageIndex(page => page - 1)}
                  isDisabled={customerPageIndex === 0}
                >
                  Anterior
                </Button>
                <Box fontSize="sm">
                  Página {customerPageIndex + 1} de {customerTotalPageCount}
                </Box>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setCustomerPageIndex(page => page + 1)}
                  isDisabled={customerPageIndex + 1 >= customerTotalPageCount}
                >
                  Próxima
                </Button>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      <RegisterHeader
        handleExportData={handleExportData}
        downloadDisabled={!users.length}
      />
      <Stack flex="1" width="100%">
        <SearchInput
          label="Filtre os Usuários"
          input={{
            placeholder: 'Pesquise...',
            value: searchString,
            onChange: setSearchString,
          }}
          filterField={{
            value: searchField,
            onChange: setSearchField,
            options: [
              { value: 'name',    label: 'Nome' },
              { value: 'email',   label: 'E-mail' },
              { value: 'phone',   label: 'Telefone' },
              { value: 'company', label: 'Empresa' },
            ],
          }}
        />
      </Stack>
      <hr />
      {isRequestLoading ? (
        <Flex justify="center" align="center" py={16}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
        </Flex>
      ) : (
        <>
          <ReactTable
            getTableBodyProps={getTableProps}
            headerGroups={headerGroups}
            rows={page}
            getTableProps={getTableProps}
            prepareRow={prepareRow}
            rowActions={[
              {
                onClick: (data: any) => {
                  handleEdit(data);
                },
                component: (
                  <IconButton aria-label="Editar usuário" icon={<Edit />} />
                ),
              },
              {
                onClick: (data: any) => {
                  handleClickButtonDelete(data);
                },
                component: (
                  <IconButton
                    variant="outline"
                    colorScheme="red"
                    aria-label="Excluir usuário"
                    icon={<Trash />}
                  />
                ),
              },
            ]}
          />

          <TableFooter
            canNextPage={canNextPage}
            canPreviousPage={canPreviousPage}
            gotoPage={gotoPage}
            nextPage={nextPage}
            pageCount={pageCount}
            pageIndex={pageIndex}
            pageOptions={pageOptions}
            previousPage={previousPage}
          />
        </>
      )}
    </Container>
  );
};

export default Users;
